import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { EEventPlatformFeature, EEventViewMode, TEventFrontendTab } from '@enums/event-tracking.enum';
import { ERoles } from '@enums/roles.enum';
import { ECampaignDetailsTab } from '@pages/campaigns/campaign-details/campaign-details.interface';

// campaign_commons' FrontendTab enum has no equivalent for the two briefing-only pseudo-tabs
// (creatorOverview, creatorTimeline) — map them down to their closest real tab before sending
// previous_tab/current_tab on an event, since the backend enum doesn't include them. Return type
// is the narrowed TEventFrontendTab so IAnalyticsEvent.previousTab/currentTab can only be assigned
// a value that's already been through this function.
const FRONTEND_TAB_TO_EVENT: Partial<Record<ECampaignDetailsTab, TEventFrontendTab>> = {
  [ECampaignDetailsTab.creatorOverview]: ECampaignDetailsTab.overview as TEventFrontendTab,
  [ECampaignDetailsTab.creatorTimeline]: ECampaignDetailsTab.contents as TEventFrontendTab,
};

export function toEventFrontendTab(tab: ECampaignDetailsTab): TEventFrontendTab {
  return FRONTEND_TAB_TO_EVENT[tab] ?? (tab as TEventFrontendTab);
}

/**
 * Converts the app's existing EPlatformFeature (camelCase values, e.g. 'instagramPost') to the
 * backend's PascalCase PlatformFeature values (e.g. 'InstagramPost') expected on analytics events.
 * Returns undefined for EPlatformFeature.facebookVideo, which has no backend equivalent.
 */
const PLATFORM_FEATURE_TO_EVENT: Partial<Record<EPlatformFeature, EEventPlatformFeature>> = {
  [EPlatformFeature.twitchStream]: EEventPlatformFeature.twitchStream,
  [EPlatformFeature.instagramPost]: EEventPlatformFeature.instagramPost,
  [EPlatformFeature.instagramStory]: EEventPlatformFeature.instagramStory,
  [EPlatformFeature.instagramReel]: EEventPlatformFeature.instagramReel,
  [EPlatformFeature.youtubeVod]: EEventPlatformFeature.youtubeVod,
  [EPlatformFeature.youtubeShort]: EEventPlatformFeature.youtubeShort,
  [EPlatformFeature.youtubeStream]: EEventPlatformFeature.youtubeStream,
  [EPlatformFeature.tiktokPost]: EEventPlatformFeature.tiktokPost,
  [EPlatformFeature.xPost]: EEventPlatformFeature.xPost,
  [EPlatformFeature.facebookPost]: EEventPlatformFeature.facebookPost,
};

export function toEventPlatformFeature(feature: EPlatformFeature): EEventPlatformFeature | undefined {
  return PLATFORM_FEATURE_TO_EVENT[feature];
}

/**
 * Converts the creators-tab view toggle's internal value ('grid'/'list') to the backend's
 * ViewMode values ('tiles'/'list') for the campaign_analytics_view_mode_changed event.
 */
export function toEventViewMode(viewType: 'grid' | 'list'): EEventViewMode {
  return viewType === 'grid' ? EEventViewMode.tiles : EEventViewMode.list;
}

/**
 * The backend invitation endpoints are per-type (`invitations/creator`, `invitations/support-user`,
 * ...), so invitation events carry the type as `source` - mapped from the invited role so the same
 * value can be derived on the accept side from the newly-created user's own roles.
 */
export type TEventInvitationSource = 'support_user' | 'campaign_user' | 'campaign_sub_user' | 'creator';

const INVITATION_ROLE_TO_SOURCE: Partial<Record<ERoles, TEventInvitationSource>> = {
  [ERoles.campaign]: 'campaign_user',
  [ERoles.campaignSub]: 'campaign_sub_user',
  [ERoles.creator]: 'creator',
  [ERoles.support]: 'support_user',
};

export function toEventInvitationSource(role: ERoles): TEventInvitationSource | undefined {
  return INVITATION_ROLE_TO_SOURCE[role];
}

/**
 * Converts the app's EDeliverableType (camelCase values, e.g. 'gamePlay') to campaign_commons'
 * Deliverable enum values (snake_case, e.g. 'game_play') for analytics events. Not a blind
 * camelCase->snake_case transform: EDeliverableType.chatBot's value is 'chatBot' but the backend
 * value is 'chatbot' (no underscore) - a generic conversion would produce 'chat_bot' and fail
 * backend validation, so this is an explicit map instead. Called centrally from
 * EventTrackingService.buildEvent() - no call site needs to convert deliverableType itself.
 */
const DELIVERABLE_TYPE_TO_EVENT: Record<EDeliverableType, string> = {
  [EDeliverableType.title]: 'title',
  [EDeliverableType.gamePlay]: 'game_play',
  [EDeliverableType.chatBot]: 'chatbot',
  [EDeliverableType.chatbotMulti]: 'chatbot_multi',
  [EDeliverableType.talkingPoint]: 'talking_point',
  [EDeliverableType.talkingPointAi]: 'talking_point_ai',
  [EDeliverableType.visual]: 'visual',
  [EDeliverableType.productPlacement]: 'product_placement',
  [EDeliverableType.showcase]: 'showcase',
  [EDeliverableType.textTracking]: 'text_tracking',
  [EDeliverableType.audioTracking]: 'audio_tracking',
  [EDeliverableType.caption]: 'caption',
  [EDeliverableType.hashTag]: 'hash_tag',
  [EDeliverableType.mention]: 'mention',
  [EDeliverableType.message]: 'message',
  [EDeliverableType.description]: 'description',
  [EDeliverableType.content]: 'content',
  [EDeliverableType.custom]: 'custom',
};

export function toEventDeliverableType(type: EDeliverableType): string {
  return DELIVERABLE_TYPE_TO_EVENT[type] ?? type;
}
